// File: assets/transformation.js
(function () {
  class BeforeAfterSliderTransformation extends HTMLElement {
    constructor() {
      super();
      this.currentSlide = 1;
      this.isDragging = false;
      // thresholds + initial
      this.AFTER_SHOW_MAX = 50; // <=50% → show After
      this.BEFORE_SHOW_MIN = 50; // >=50% → show Before
      this.INITIAL_PCT = 0;      // start far left
    }

    connectedCallback() {
      this.slides = this.querySelectorAll('.tcw-before-after-slider__slide-transformation');
      this.thumbnails = this.querySelectorAll('.tcw-before-after-slider__thumbnail-transformation');
      this.containers = this.querySelectorAll('.tcw-before-after-container-transformation');
      this.prevArrow = this.querySelector('.tcw-before-after-arrow--prev');
      this.nextArrow = this.querySelector('.tcw-before-after-arrow--next');
      this.slidesCount = parseInt(this.getAttribute('data-slides-count') || '0', 10);

      if (![...this.slides].some(s => s.classList.contains('active')) && this.slides.length) {
        this.slides[0].classList.add('active');
        this.slides.forEach((s, i) => s.toggleAttribute('hidden', i !== 0));
        this.thumbnails[0]?.classList.add('active');
        this.thumbnails.forEach((t, i) => t.setAttribute('aria-selected', String(i === 0)));
      }

      this.setupThumbnails();
      this.setupBeforeAfter();
      this.setupKeyboard();
      this.setupArrows();
    }

    /* === Arrows now change slides (not scroll thumbnails) === */
    setupArrows() {
      if (!this.prevArrow && !this.nextArrow) return;

      const updateArrowDisabled = () => {
        const disable = (this.slides?.length || 0) <= 1;
        if (this.prevArrow) this.prevArrow.disabled = disable;
        if (this.nextArrow) this.nextArrow.disabled = disable;
      };

      // Click → move to prev/next slide (wraps via goToSlide)
      this.prevArrow?.addEventListener('click', (e) => {
        e.preventDefault();
        this.goToSlide(this.currentSlide - 1);
      });
      this.nextArrow?.addEventListener('click', (e) => {
        e.preventDefault();
        this.goToSlide(this.currentSlide + 1);
      });

      updateArrowDisabled();
    }

    /* === helpers for ensuring active thumb is visible === */
    getThumbPageWidth(track) {
      const thumbs = track.querySelectorAll('.tcw-before-after-slider__thumbnail-transformation');
      if (!thumbs.length) return track.clientWidth;

      const cs = getComputedStyle(track);
      const gap = parseFloat(cs.columnGap || cs.gap || '12') || 12;
      const perViewVar = cs.getPropertyValue('--thumbs-per-view').trim();
      const perView = Number(perViewVar) || 4;

      const sampleW = thumbs[0].getBoundingClientRect().width || 0;
      if (!sampleW) return track.clientWidth;

      return (sampleW * perView) + (gap * (perView - 1));
    }

    ensureActiveThumbVisible(targetIndexZeroBased) {
      const track = this.querySelector('.tcw-before-after-slider__thumbnails-transformation');
      const thumb = this.thumbnails?.[targetIndexZeroBased];
      if (!track || !thumb) return;

      const tRect = track.getBoundingClientRect();
      const iRect = thumb.getBoundingClientRect();

      // Why: keep focus/selection context in view for users
      if (iRect.left < tRect.left) {
        track.scrollBy({ left: iRect.left - tRect.left - 12, behavior: 'smooth' });
      } else if (iRect.right > tRect.right) {
        track.scrollBy({ left: iRect.right - tRect.right + 12, behavior: 'smooth' });
      }
    }

    setupThumbnails() {
      this.thumbnails.forEach((thumbnail) => {
        thumbnail.addEventListener('click', () => {
          this.goToSlide(parseInt(thumbnail.dataset.slide, 10));
        });

        thumbnail.addEventListener('keydown', (e) => {
          // ARIA tabs pattern keyboard navigation
          if (e.key === 'ArrowRight' || e.key === 'ArrowLeft' || e.key === 'Home' || e.key === 'End') {
            e.preventDefault();
            const idx = [...this.thumbnails].indexOf(thumbnail) + 1;
            if (e.key === 'ArrowRight') this.goToSlide(idx === this.slides.length ? 1 : idx + 1);
            if (e.key === 'ArrowLeft') this.goToSlide(idx === 1 ? this.slides.length : idx - 1);
            if (e.key === 'Home') this.goToSlide(1);
            if (e.key === 'End') this.goToSlide(this.slides.length);
            this.querySelector('.tcw-before-after-slider__thumbnail-transformation.active')?.focus();
          }
        });
      });
    }

    // Show/hide labels in a container based on pct
    applyLabelState(container, pct) {
      const labelsWrap = container.querySelector('.tcw-before-after-labels-transformation');
      const beforeLabelEl = container.querySelector('.tcw-the-before-label');
      const afterLabelEl  = container.querySelector('.tcw-the-after-label');

      const setLabelVisibility = (el, show) => {
        if (!el) return;
        el.classList.toggle('is-visible', !!show);
        el.setAttribute('aria-hidden', show ? 'false' : 'true');
      };

      const showAfter  = pct <= this.AFTER_SHOW_MAX;
      const showBefore = pct >= this.BEFORE_SHOW_MIN;

      setLabelVisibility(afterLabelEl, showAfter);
      setLabelVisibility(beforeLabelEl, showBefore);
      labelsWrap?.setAttribute('aria-hidden', String(!(showAfter || showBefore)));
    }

    setupBeforeAfter() {
      this.containers.forEach((container) => {
        const handle = container.querySelector('.tcw-before-after-handle-transformation');
        const divider = container.querySelector('.tcw-before-after-divider-transformation');
        const afterImage = container.querySelector('.tcw-before-after-after-transformation');
        if (!handle || !divider || !afterImage) return;

        const setFromPercent = (pct, { updateLabels = true } = {}) => {
          const clamped = Math.max(0, Math.min(100, pct));
          divider.style.left = clamped + '%';
          handle.style.left = clamped + '%';
          afterImage.style.clipPath = `inset(0 ${100 - clamped}% 0 0)`;
          handle.setAttribute('aria-valuenow', String(Math.round(clamped)));
          if (updateLabels) this.applyLabelState(container, clamped);
        };

        const updateFromClientX = (clientX) => {
          const rect = container.getBoundingClientRect();
          const x = clientX - rect.left;
          setFromPercent((x / rect.width) * 100);
        };

        // Init: position at far left and show the proper label (After)
        setFromPercent(this.INITIAL_PCT, { updateLabels: false });
        this.applyLabelState(container, this.INITIAL_PCT);

        // Mouse
        handle.addEventListener('mousedown', (e) => { this.isDragging = true; e.preventDefault(); });
        container.addEventListener('mousemove', (e) => { if (this.isDragging) updateFromClientX(e.clientX); });
        container.addEventListener('click', (e) => { if (!this.isDragging) updateFromClientX(e.clientX); });
        document.addEventListener('mouseup', () => { this.isDragging = false; });

        // Touch
        handle.addEventListener('touchstart', (e) => { this.isDragging = true; e.preventDefault(); }, { passive:false });
        container.addEventListener('touchmove', (e) => {
          if (this.isDragging && e.touches[0]) { updateFromClientX(e.touches[0].clientX); e.preventDefault(); }
        }, { passive:false });
        document.addEventListener('touchend', () => { this.isDragging = false; });

        // Keyboard
        handle.addEventListener('keydown', (e) => {
          const step = 5;
          const current = parseFloat(handle.getAttribute('aria-valuenow')) || this.INITIAL_PCT;
          if (e.key === 'ArrowRight') { e.preventDefault(); setFromPercent(current + step); }
          if (e.key === 'ArrowLeft')  { e.preventDefault(); setFromPercent(current - step); }
          if (e.key === 'Home')       { e.preventDefault(); setFromPercent(0); }
          if (e.key === 'End')        { e.preventDefault(); setFromPercent(100); }
        });
      });
    }

    setupKeyboard() {
      // slide navigation anywhere inside component (not the handle)
      this.addEventListener('keydown', (e) => {
        if (e.defaultPrevented) return;
        if (e.target.closest('.tcw-before-after-handle-transformation')) return;
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) return;
        e.preventDefault();
        if (e.key === 'ArrowRight') this.goToSlide(this.currentSlide + 1);
        if (e.key === 'ArrowLeft')  this.goToSlide(this.currentSlide - 1);
        if (e.key === 'Home')       this.goToSlide(1);
        if (e.key === 'End')        this.goToSlide(this.slides.length);
        this.querySelector('.tcw-before-after-slider__thumbnail-transformation.active')?.focus();
      });
    }

    goToSlide(slideNumber) {
      if (!this.slides.length) return;
      const total = this.slides.length;
      const target = ((slideNumber - 1 + total) % total) + 1; // wrap

      this.slides.forEach((s, i) => {
        const active = (i + 1) === target;
        s.classList.toggle('active', active);
        s.toggleAttribute('hidden', !active);
      });
      this.thumbnails.forEach((t, i) => {
        const active = (i + 1) === target;
        t.classList.toggle('active', active);
        t.setAttribute('aria-selected', String(active));
      });

      this.currentSlide = target;

      // Reset new slide: far left + show correct label (After)
      const activeSlide = this.slides[target - 1];
      const container = activeSlide?.querySelector('.tcw-before-after-container-transformation');
      const handle = container?.querySelector('.tcw-before-after-handle-transformation');
      const divider = container?.querySelector('.tcw-before-after-divider-transformation');
      const afterImage = container?.querySelector('.tcw-before-after-after-transformation');
      if (container && handle && divider && afterImage) {
        const pct = this.INITIAL_PCT;
        divider.style.left = pct + '%';
        handle.style.left = pct + '%';
        afterImage.style.clipPath = `inset(0 ${100 - pct}% 0 0)`;
        handle.setAttribute('aria-valuenow', String(pct));
        this.applyLabelState(container, pct);
      }

      // Keep active thumbnail visible
      this.ensureActiveThumbVisible(target - 1);
    }
  }

  customElements.define('before-after-slider-transformation', BeforeAfterSliderTransformation);
})();
